import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../auth/AuthContext';
import { adminTotpApi } from '../services/api';

interface EnrolledUser {
  oid: string;
  name: string;
  email: string;
}

const AdminTotpPage: React.FC = () => {
  const { user } = useAuth();

  if (!user?.roles?.includes('reapertura_autorizada')) {
    return <Navigate to="/" replace />;
  }

  return <AdminTotpPageInner />;
};

const AdminTotpPageInner: React.FC = () => {
  const [enrolled, setEnrolled] = useState<EnrolledUser[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  const [oid, setOid] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');

  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<EnrolledUser | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState('');

  const fetchList = async () => {
    setLoadingList(true);
    setListError('');
    try {
      const data = await adminTotpApi.list();
      setEnrolled(data.enrolled);
    } catch (err: any) {
      setListError(err?.response?.data?.message || err?.message || 'Error al cargar la lista');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleEnrollSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnrollError('');
    setEnrolling(true);
    try {
      const data = await adminTotpApi.enroll(oid, name, email);
      setOtpauthUri(data.otpauth_uri);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Error al enrolar el usuario';
      setEnrollError(msg);
    } finally {
      setEnrolling(false);
    }
  };

  const handleQrDismiss = () => {
    setOtpauthUri(null);
    setOid('');
    setName('');
    setEmail('');
    fetchList();
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setRevokeError('');
    try {
      await adminTotpApi.revoke(revokeTarget.oid);
      setEnrolled((prev) => prev.filter((u) => u.oid !== revokeTarget.oid));
      setRevokeTarget(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Error al revocar el usuario';
      setRevokeError(msg);
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="p-4">
      <h2>Gestión TOTP</h2>

      <section className="mb-5">
        <h4>Usuarios enrolados</h4>
        {loadingList && <Spinner animation="border" size="sm" />}
        {listError && <Alert variant="danger">{listError}</Alert>}
        {!loadingList && !listError && (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>OID</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {enrolled.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    No hay usuarios enrolados
                  </td>
                </tr>
              ) : (
                enrolled.map((u) => (
                  <tr key={u.oid}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td title={u.oid}>{u.oid.slice(0, 8)}…</td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setRevokeError('');
                          setRevokeTarget(u);
                        }}
                      >
                        Revocar
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </section>

      <section className="mb-5">
        <h4>Enrolar usuario</h4>
        {enrollError && <Alert variant="danger">{enrollError}</Alert>}
        <Form onSubmit={handleEnrollSubmit} style={{ maxWidth: 480 }}>
          <Form.Group className="mb-3" controlId="enroll-oid">
            <Form.Label>OID (Azure AD Object ID)</Form.Label>
            <Form.Control
              type="text"
              value={oid}
              onChange={(e) => setOid(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="enroll-name">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre completo"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="enroll-email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@empresa.com"
              required
            />
          </Form.Group>
          <Button type="submit" variant="primary" disabled={enrolling}>
            {enrolling ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enrolando...
              </>
            ) : (
              'Enrolar'
            )}
          </Button>
        </Form>
      </section>

      {/* QR modal */}
      <Modal show={!!otpauthUri} backdrop="static" onHide={() => {}}>
        <Modal.Header>
          <Modal.Title>Código QR de enrolamiento</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {otpauthUri && (
            <>
              <div className="d-flex justify-content-center mb-3">
                <QRCodeSVG value={otpauthUri} size={256} />
              </div>
              <Alert variant="warning">
                Este QR no podrá recuperarse. Escanéalo ahora antes de cerrar.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleQrDismiss}>Ya escaneé el código</Button>
        </Modal.Footer>
      </Modal>

      {/* Revoke confirm modal */}
      <Modal show={!!revokeTarget} onHide={() => setRevokeTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Revocar usuario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {revokeError && <Alert variant="danger">{revokeError}</Alert>}
          {revokeTarget && (
            <p>
              ¿Revocar a {revokeTarget.name} ({revokeTarget.email})?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setRevokeTarget(null);
              setRevokeError('');
            }}
            disabled={revoking}
          >
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleRevokeConfirm} disabled={revoking}>
            {revoking ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Revocando...
              </>
            ) : (
              'Revocar'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminTotpPage;
